import { DateTime } from 'luxon'

// ADR 0015 (invi-wiki): a recurring series runs at most 3 calendar months ahead
// (Europe/Amsterdam) and can be extended from 7 days before its last date.
export const MAX_SERIES_MONTHS = 3
export const EXTEND_DAYS_BEFORE_END = 7
export const SUPPORTED_RECURRING = ['7,day', '14,day', '1,month']
export const ZONE = 'Europe/Amsterdam'

// ISO weekdays, 1 = maandag … 7 = zondag
export const WEEKDAYS = [
  { key: 1, short: 'ma', label: 'Ma' },
  { key: 2, short: 'di', label: 'Di' },
  { key: 3, short: 'wo', label: 'Wo' },
  { key: 4, short: 'do', label: 'Do' },
  { key: 5, short: 'vr', label: 'Vr' },
  { key: 6, short: 'za', label: 'Za' },
  { key: 7, short: 'zo', label: 'Zo' }
]

const toDateTime = (value) => {
  if (!value) return null
  if (DateTime.isDateTime(value)) return value.setZone(ZONE)
  if (value instanceof Date) return DateTime.fromJSDate(value, { zone: ZONE })
  const dt = DateTime.fromISO(String(value), { zone: ZONE })
  return dt.isValid ? dt : null
}

// calendar day (start of day, Europe/Amsterdam)
export const toDay = (value) => {
  const dt = toDateTime(value)
  return dt ? dt.startOf('day') : null
}

// last calendar day a series starting on `startDate` may reach (start + 3 months)
export const seriesMaxEndDay = (startDate) => {
  const day = toDay(startDate)
  return day ? day.plus({ months: MAX_SERIES_MONTHS }) : null
}

export const isEndDateWithinLimit = (startDate, endDate) => {
  const max = seriesMaxEndDay(startDate)
  const end = toDay(endDate)
  return !max || !end || end <= max
}

export const isoWeekday = (date) => {
  const day = toDay(date)
  return day ? day.weekday : null
}

// the weekdays to send: the chosen ones plus the weekday of the start date, sorted
export const effectiveWeekdays = (selected, startDate) => {
  const start = isoWeekday(startDate)
  const days = new Set((selected || []).map(Number))
  if (start) days.add(start)
  return [...days].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b)
}

// 'di en do', 'ma, wo en vr'
export const formatWeekdays = (weekdays) => {
  const names = (weekdays || [])
    .map((d) => WEEKDAYS.find((w) => w.key === Number(d)))
    .filter(Boolean)
    .map((w) => w.short)
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} en ${names[names.length - 1]}`
}

const formatDay = (day) =>
  day ? day.setLocale('nl').toFormat('d LLLL yyyy') : ''

// Readable Dutch message for a RECURRING_* API error, or null for any other error.
export const recurringErrorMessage = (error) => {
  const code = error && error.code
  if (!code || !String(code).startsWith('RECURRING_')) return null
  const maxEndDate =
    error.properties && error.properties.maxEndDate
      ? toDay(error.properties.maxEndDate)
      : null
  switch (code) {
    case 'RECURRING_UNSUPPORTED':
      return 'Deze herhaling wordt niet meer ondersteund. Kies wekelijks, tweewekelijks of maandelijks.'
    case 'RECURRING_END_DATE_REQUIRED':
      return 'Kies tot en met welke datum de activiteit herhaalt.'
    case 'RECURRING_END_BEFORE_START':
      return 'De einddatum ligt voor de startdatum.'
    case 'RECURRING_TOO_LONG':
      return maxEndDate
        ? `Een reeks loopt maximaal 3 maanden door, dus tot en met ${formatDay(
            maxEndDate
          )}.`
        : 'Een reeks loopt maximaal 3 maanden door.'
    case 'RECURRING_WEEKDAYS_NOT_ALLOWED':
      return 'Je kunt alleen bij een wekelijkse herhaling meerdere dagen kiezen.'
    case 'RECURRING_WEEKDAYS_INVALID':
      return 'De gekozen weekdagen kloppen niet. Kies de dagen opnieuw.'
    case 'RECURRING_START_NOT_IN_WEEKDAYS':
      return 'De dag van de startdatum moet een van de gekozen weekdagen zijn.'
    case 'RECURRING_NOT_EXTENDABLE':
      return 'Deze reeks kan niet verlengd worden.'
    case 'RECURRING_END_NOT_AFTER_CURRENT':
      return 'Deze reeks loopt al tot die datum.'
    case 'RECURRING_END_DATE_INVALID':
      return 'De nieuwe einddatum is ongeldig.'
    default:
      return 'De herhaling van deze activiteit klopt niet. Controleer de datums.'
  }
}

// the latest date of a series (calendar day), from the allDates of /date/eventdate
export const lastSeriesDay = (allDates) => {
  const days = (allDates || [])
    .map((d) => toDay(d && d.startDateTime))
    .filter(Boolean)
  if (!days.length) return null
  return days.reduce((a, b) => (b > a ? b : a))
}

// default (and maximum) end date when extending: last date + 3 months
export const extendTargetDay = (allDates) => {
  const last = lastSeriesDay(allDates)
  return last ? last.plus({ months: MAX_SERIES_MONTHS }) : null
}

// Owner sees "Reeks verlengen" from 7 days before the last date of a supported, active series.
export const canExtendSeries = (eventDate, authId, now = DateTime.local()) => {
  if (!eventDate || !authId) return false
  if (Number(eventDate.ownerId) !== Number(authId)) return false
  if (!SUPPORTED_RECURRING.includes(eventDate.recurring)) return false
  if (eventDate.cancelled) return false
  const last = lastSeriesDay(eventDate.allDates)
  if (!last) return false
  const today = toDay(now)
  return today >= last.minus({ days: EXTEND_DAYS_BEFORE_END })
}

export const formatSeriesDay = formatDay
